import express, { Application } from 'express';
import { Logger } from '@unidev-hub/logger';
import { ConfigManager } from '@unidev-hub/config';
import { ApiOptions, ApiResult } from '../types';
import {
  applyRegularMiddleware,
  applyErrorHandlers,
  isMiddlewareEnabled
} from '../middleware';
import { setupAuth } from '../middleware/auth';
import { metricsMiddleware } from '../middleware/metrics';

// Import factories to ensure all middleware is registered
import '../middleware/factories';
import {MetricsOptions} from "../middleware/types";

/**
 * Creates and configures an Express application with middleware
 */
export async function createApi(
  config: ConfigManager,
  logger: Logger,
  options: ApiOptions = {}
): Promise<ApiResult> {
  // Create Express application
  const app = express();
  const basePath = options.basePath || config.get('api.basePath', '/api');

  logger.debug('Creating API application', { basePath });

  // Store basePath in app.locals
  app.locals.basePath = basePath;

  // Apply all regular middleware
  applyRegularMiddleware(app, config, logger);

  // Special handling for auth middleware
  if (isMiddlewareEnabled('auth', config)) {
    try {
      app.locals.authMiddleware = await setupAuth(
        app,
        config,
        logger,
        config.get('api.auth', {})
      );
      logger.debug('Authentication middleware initialized');
    } catch (error) {
      logger.error('Failed to initialize auth middleware:', error as Error);
      const authRequired = config.get('api.auth.required', false);
      if (authRequired) {
        throw error;
      }
    }
  }

  // Handle metrics endpoint separately if enabled
  if (isMiddlewareEnabled('metrics', config)) {
    const metricsConfig = config.get<MetricsOptions>('api.metrics', {});
    const endpoint = metricsConfig.endpoint || '/metrics';

    // Safely handle the metrics endpoint
    try {
      const metricsResult = metricsMiddleware(metricsConfig);
      if (metricsResult && typeof metricsResult === 'object' && 'endpoint' in metricsResult) {
        app.get(endpoint, metricsResult.endpoint);
        logger.debug(`Metrics endpoint registered at: ${endpoint}`);
      } else {
        logger.warn('Metrics middleware did not return expected endpoint handler');
      }
    } catch (error) {
      logger.error('Failed to set up metrics endpoint:', error as Error);
    }
  }

  // Add pre-route middlewares if provided
  if (options.preMiddlewares && options.preMiddlewares.length > 0) {
    app.use(options.preMiddlewares);
    logger.debug(`Added ${options.preMiddlewares.length} pre-middlewares`);
  }

  // Function to mount routes
  const mountRoutes = (router: express.Router) => {
    app.use(basePath, router);
    logger.debug(`Mounted routes to base path: ${basePath}`);

    // Add post-route middlewares if provided
    if (options.postMiddlewares && options.postMiddlewares.length > 0) {
      app.use(options.postMiddlewares);
      logger.debug(`Added ${options.postMiddlewares.length} post-middlewares`);
    }

    // Apply error handlers after routes
    applyErrorHandlers(app, config, logger);
  };

  logger.info('API application created successfully');

  return { app, mountRoutes };
}
